/**
 * PE Diligence TUI Web - Main Application
 */

import { useState, useCallback, useEffect } from 'react'
import { Header } from './components/layout/Header'
import { TabNav } from './components/layout/TabNav'
import { StatusBar } from './components/layout/StatusBar'
import { FinancialsPanel } from './components/panels/FinancialsPanel'
import { FCFPanel } from './components/panels/FCFPanel'
import { RegimePanel } from './components/panels/RegimePanel'
import { ChatPanel } from './components/panels/ChatPanel'
import { MAPanel } from './components/panels/MAPanel'
import { DDReportPanel } from './components/panels/DDReportPanel'
import { DataRoomPanel } from './components/panels/DataRoomPanel'
import { HistoryPanel } from './components/panels/HistoryPanel'
import { DEFAULT_TABS, type CompanyData, type StatusState } from './types'
import { getCompany, getFinancials, getRegime, checkHealth, searchCompanies, type ChatContext, type FinancialData, type RegimeData } from './services/api'

export function App() {
  const [activeTab, setActiveTab] = useState('financials')
  const [company, setCompany] = useState<CompanyData | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(true)
  const [isPrivateMode, setIsPrivateMode] = useState(false)
  const [status, setStatus] = useState<StatusState>({
    message: 'Connecting to API...',
    type: 'loading',
  })
  const [_apiReady, setApiReady] = useState(false)

  // Additional state for chat context
  const [latestFinancials, setLatestFinancials] = useState<FinancialData | null>(null)
  const [regimeData, setRegimeData] = useState<RegimeData | null>(null)

  // Check API health on startup
  useEffect(() => {
    const init = async () => {
      try {
        const result = await checkHealth()
        if (result.data) {
          setApiReady(true)
          setStatus({
            message: 'Ready - Enter a ticker symbol to analyze',
            type: 'info',
          })
        } else {
          setStatus({
            message: 'API unavailable - using local mode',
            type: 'warning',
          })
        }
      } catch {
        setStatus({
          message: 'API unavailable - using local mode',
          type: 'warning',
        })
      }
    }
    init()
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Quit on Ctrl+Q or Escape
      if ((e.key === 'q' && e.ctrlKey) || e.key === 'Escape') {
        if (!isSearchFocused) {
          setIsSearchFocused(true)
        }
        return
      }

      // Tab switching with number keys (when not in input)
      if (!isSearchFocused && e.key >= '1' && e.key <= '8') {
        const tabIndex = parseInt(e.key) - 1
        if (tabIndex < DEFAULT_TABS.length) {
          setActiveTab(DEFAULT_TABS[tabIndex].id)
        }
      }

      // Focus search with /
      if (e.key === '/' && !isSearchFocused) {
        e.preventDefault()
        setIsSearchFocused(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isSearchFocused])

  // Handle search/analyze
  const handleSearch = useCallback((value: string) => {
    setSearchValue(value)
  }, [])

  const handleAnalyze = useCallback(async () => {
    if (!searchValue.trim()) {
      setStatus({ message: 'Please enter a company name or ticker', type: 'warning' })
      return
    }

    const query = searchValue.trim()

    // Handle private company mode
    if (isPrivateMode) {
      setStatus({ message: `Setting up private company: "${query}"...`, type: 'loading' })

      // Create a sanitized ticker from the company name (e.g., "Schumacher Electric" -> "SCHUMACHER-ELECTRIC")
      const sanitizedTicker = query
        .toUpperCase()
        .replace(/[^A-Z0-9\s]/g, '')
        .replace(/\s+/g, '-')
        .substring(0, 20) // Limit length

      setCompany({
        ticker: sanitizedTicker,
        name: query,
        sector: 'Private',
        industry: 'Unknown',
        marketCap: 0,
        description: `Private company: ${query}. Limited public data available.`,
        quoteType: 'PRIVATE',
        isPrivate: true,
      })

      // Clear any cached financial data for private companies
      setLatestFinancials(null)
      setRegimeData(null)

      setStatus({
        message: `Loaded ${query} as private company - use Data Room for document collection`,
        type: 'success',
      })

      // Auto-switch to financials tab (which will show the private company banner)
      setActiveTab('financials')
      setIsSearchFocused(false)
      return
    }

    // Standard public company search flow
    setStatus({ message: `Searching for "${query}"...`, type: 'loading' })

    try {
      // First, search for the company
      const searchResult = await searchCompanies(query)

      if (searchResult.error) {
        // Fallback to direct ticker lookup
        const ticker = query.toUpperCase()
        const result = await getCompany(ticker)

        if (result.error) {
          setCompany({
            ticker,
            name: `${ticker} Corporation`,
            sector: 'Technology',
            industry: 'Software',
            marketCap: 1000000000,
            description: `Company data for ${ticker}`,
          })
          setStatus({ message: `Loaded local data for ${ticker}`, type: 'success' })
        } else if (result.data) {
          setCompany({
            ticker: result.data.ticker,
            name: result.data.name,
            sector: result.data.sector,
            industry: result.data.industry,
            marketCap: result.data.marketCap,
            description: result.data.description,
          })
          setStatus({ message: `Loaded data for ${ticker}`, type: 'success' })
        }
      } else if (searchResult.data) {
        const { matches, total_count } = searchResult.data

        if (matches.length === 0) {
          setStatus({ message: `No companies found for "${query}"`, type: 'warning' })
          return
        }

        // Use the first (best) match
        const bestMatch = matches[0]
        const ticker = bestMatch.ticker

        // If multiple matches, note it in status
        const matchNote = total_count > 1
          ? ` (${total_count} matches found)`
          : ''

        setStatus({ message: `Loading ${bestMatch.name}${matchNote}...`, type: 'loading' })

        // Fetch full company data for the selected ticker
        const result = await getCompany(ticker)

        if (result.error) {
          // Use search result data as fallback
          setCompany({
            ticker: bestMatch.ticker,
            name: bestMatch.name,
            sector: bestMatch.sector || 'Unknown',
            industry: 'Unknown',
            marketCap: 0,
            description: `Company data for ${bestMatch.name}`,
          })
          setStatus({ message: `Loaded ${bestMatch.name} (${ticker})${matchNote}`, type: 'success' })
        } else if (result.data) {
          setCompany({
            ticker: result.data.ticker,
            name: result.data.name,
            sector: result.data.sector,
            industry: result.data.industry,
            marketCap: result.data.marketCap,
            description: result.data.description,
          })
          setStatus({ message: `Loaded ${result.data.name} (${ticker})${matchNote}`, type: 'success' })
        }

        // Update search value to show the resolved ticker
        setSearchValue(ticker)
      }

      // Auto-switch to financials tab when company is found
      setActiveTab('financials')
      setIsSearchFocused(false)
    } catch (error) {
      console.error('Analysis error:', error)
      setStatus({ message: `Error searching for "${query}"`, type: 'error' })
    }
  }, [searchValue, isPrivateMode])

  // Handle tab change
  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId)
    setIsSearchFocused(false)
  }, [])

  // Handle selecting a company from history
  const handleSelectCompany = useCallback((ticker: string, companyName: string) => {
    setCompany({
      ticker,
      name: companyName,
      sector: 'Technology',
      industry: 'Software',
      marketCap: 1000000000,
      description: `Company data for ${ticker}`,
    })
    setSearchValue(ticker)
    setActiveTab('financials')
    setStatus({ message: `Loaded ${ticker} from history`, type: 'success' })
  }, [])

  // Handle re-analyze from history
  const handleReanalyze = useCallback(async (ticker: string) => {
    setSearchValue(ticker)
    setStatus({ message: `Re-analyzing ${ticker}...`, type: 'loading' })

    try {
      const result = await getCompany(ticker)

      if (result.error) {
        setCompany({
          ticker,
          name: `${ticker} Corporation`,
          sector: 'Technology',
          industry: 'Software',
          marketCap: 1000000000,
          description: `Company data for ${ticker}`,
        })
        setStatus({ message: `Re-analyzed ${ticker} (local mode)`, type: 'success' })
      } else if (result.data) {
        setCompany({
          ticker: result.data.ticker,
          name: result.data.name,
          sector: result.data.sector,
          industry: result.data.industry,
          marketCap: result.data.marketCap,
          description: result.data.description,
        })
        setStatus({ message: `Re-analyzed ${ticker}`, type: 'success' })
      }

      setActiveTab('financials')
      setIsSearchFocused(false)
    } catch (error) {
      console.error('Re-analysis error:', error)
      setStatus({ message: `Error re-analyzing ${ticker}`, type: 'error' })
    }
  }, [])

  // Fetch additional context data when company changes
  // Skip for private companies since they don't have public financial data
  useEffect(() => {
    const fetchContextData = async () => {
      if (!company?.ticker) return

      // Skip API calls for private companies
      if (company.isPrivate) {
        setLatestFinancials(null)
        setRegimeData(null)
        return
      }

      try {
        // Fetch financials and regime in parallel
        const [financialsResult, regimeResult] = await Promise.all([
          getFinancials(company.ticker),
          getRegime(company.ticker),
        ])

        if (financialsResult.data) {
          setLatestFinancials(financialsResult.data)
        }

        if (regimeResult.data) {
          setRegimeData(regimeResult.data)
        }
      } catch (error) {
        console.log('Could not fetch context data:', error)
      }
    }

    fetchContextData()
  }, [company?.ticker, company?.isPrivate])

  // Build chat context from company, financials, and regime data
  const buildChatContext = useCallback((): ChatContext => {
    return {
      ticker: company?.ticker,
      name: company?.name,
      sector: company?.sector,
      industry: company?.industry,
      marketCap: company?.marketCap,
      revenue: latestFinancials?.revenue,
      fcf: latestFinancials?.freeCashFlow,
      regime: regimeData?.currentRegime,
    }
  }, [company, latestFinancials, regimeData])

  // Handle chat message (status update callback from ChatPanel)
  const handleSendChatMessage = useCallback((message: string) => {
    setStatus({ message: `Sending: ${message.substring(0, 30)}...`, type: 'loading' })
    // The actual API call is now handled by ChatPanel
    // This callback is just for status updates
    setTimeout(() => {
      setStatus({ message: 'Message sent to AI assistant', type: 'success' })
    }, 500)
  }, [])

  // Render active panel
  const renderPanel = () => {
    const panelProps = { company }

    switch (activeTab) {
      case 'financials':
        return <FinancialsPanel {...panelProps} />
      case 'fcf':
        return <FCFPanel {...panelProps} />
      case 'regime':
        return <RegimePanel {...panelProps} />
      case 'chat':
        return <ChatPanel {...panelProps} context={buildChatContext()} onSendMessage={handleSendChatMessage} />
      case 'ma':
        return <MAPanel {...panelProps} />
      case 'report':
        return <DDReportPanel {...panelProps} />
      case 'dataroom':
        return <DataRoomPanel {...panelProps} />
      case 'history':
        return <HistoryPanel onSelectCompany={handleSelectCompany} onReanalyze={handleReanalyze} />
      default:
        return (
          <div className="flex items-center justify-center h-full text-term-textDim">
            {company
              ? `${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} panel for ${company.ticker}`
              : 'Enter a ticker symbol above to begin analysis'}
          </div>
        )
    }
  }

  return (
    <div className="h-screen flex flex-col bg-term-bgDark">
      {/* Header with search */}
      <Header
        company={company}
        searchValue={searchValue}
        onSearchChange={handleSearch}
        onAnalyze={handleAnalyze}
        isSearchFocused={isSearchFocused}
        onSearchFocus={() => setIsSearchFocused(true)}
        onSearchBlur={() => setIsSearchFocused(false)}
        isPrivateMode={isPrivateMode}
        onPrivateModeChange={setIsPrivateMode}
      />

      {/* Tab Navigation */}
      <TabNav
        tabs={DEFAULT_TABS}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Main Content Area */}
      <main className="flex-1 overflow-hidden p-4">
        <div className="h-full term-box p-4 overflow-auto">
          {renderPanel()}
        </div>
      </main>

      {/* Status Bar */}
      <StatusBar message={status.message} type={status.type} />
    </div>
  )
}
